import { useRef, useState } from "react";
import { cloudinaryConfigured, uploadImage } from "../lib/cloudinary";

type Props = {
  value: string | null;
  onChange: (url: string | null) => void;
  label?: string;
  /** "circle" (avatar) or "rect" (book/event cover). Default: circle. */
  shape?: "circle" | "rect";
  /** For rect shape — defaults to 2/3 (book cover). Ignored when circle. */
  aspectRatio?: string;
  /** Pixel size for the preview. Default 64 (circle) / 120 (rect). */
  previewSize?: number;
};

export default function PhotoUpload({
  value,
  onChange,
  label = "Photo",
  shape = "circle",
  aspectRatio = "2/3",
  previewSize,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const size = previewSize ?? (shape === "circle" ? 64 : 120);

  if (!cloudinaryConfigured) {
    return (
      <div style={{ marginBottom: "1rem" }}>
        <div
          className="eyebrow"
          style={{ color: "var(--c-palm)", fontSize: "0.7rem", marginBottom: "0.4rem" }}
        >
          {label}
        </div>
        <div
          style={{
            padding: "0.85rem 1rem",
            border: "1px dashed var(--c-rule)",
            borderRadius: "var(--r-sm)",
            background: "rgba(18, 63, 56, 0.04)",
            fontSize: "0.85rem",
            color: "var(--c-palm)",
            opacity: 0.75,
          }}
        >
          Image upload isn't set up yet — leave this blank, you can add one later.
        </div>
      </div>
    );
  }

  async function onPick(file: File) {
    setError(null);
    if (file.size > 8 * 1024 * 1024) {
      setError("That file is over 8 MB. Try a smaller one.");
      return;
    }
    setUploading(true);
    try {
      const result = await uploadImage(file);
      onChange(result.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  const previewBoxStyle: React.CSSProperties =
    shape === "circle"
      ? {
          width: size,
          height: size,
          borderRadius: "50%",
        }
      : {
          width: size,
          aspectRatio,
          borderRadius: "var(--r-sm)",
        };

  return (
    <div style={{ marginBottom: "1rem" }}>
      <div
        className="eyebrow"
        style={{ color: "var(--c-palm)", fontSize: "0.7rem", marginBottom: "0.4rem" }}
      >
        {label}
      </div>
      <div style={{ display: "flex", gap: "0.85rem", alignItems: "center" }}>
        {value ? (
          <img
            src={value}
            alt="Uploaded preview"
            style={{
              ...previewBoxStyle,
              objectFit: "cover",
              border: "2px solid var(--c-sand)",
              boxShadow: "var(--shadow-soft)",
            }}
          />
        ) : (
          <div
            aria-hidden="true"
            style={{
              ...previewBoxStyle,
              background: "var(--c-blush)",
              border: "1px dashed var(--c-rule)",
            }}
          />
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              const file = e.currentTarget.files?.[0];
              if (file) void onPick(file);
              e.currentTarget.value = "";
            }}
          />
          <button
            type="button"
            className="btn btn--ghost"
            style={{ padding: "0.55rem 1rem", fontSize: "0.78rem" }}
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? "Uploading…" : value ? "Change image" : "Upload image"}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange(null)}
              style={{
                background: "none",
                border: 0,
                color: "var(--c-coral)",
                fontSize: "0.8rem",
                textDecoration: "underline",
                cursor: "pointer",
                padding: 0,
                textAlign: "left",
              }}
            >
              Remove
            </button>
          )}
        </div>
      </div>
      {error && (
        <div
          role="alert"
          style={{ color: "#9a3514", fontSize: "0.85rem", marginTop: "0.5rem" }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
